<template>
  <b-modal
    v-model="visible"
    title="Import Excel — Chi tiết khoản mục phí"
    size="xl"
    no-close-on-backdrop
    @hidden="$emit('close')"
  >
    <div v-if="!previewRows.length">
      <b-alert show variant="info">
        <strong>Bước 1:</strong> Chuẩn bị dữ liệu đúng định dạng, hoặc tải file mẫu
        <a :href="urlTemplate">tại đây</a>.
        <br />Cột A = <em>Khoản mục phí</em>, B = <em>Cửa hàng</em>,
        C = <em>Số tiền đề nghị</em>, D = <em>Số tháng SD</em> (tùy chọn, mặc định 1).
        <br />Dữ liệu bắt đầu từ hàng 2 (hàng 1 là header). Tên phải khớp chính xác sau khi bỏ dấu.
      </b-alert>
      <b-form-file
        v-model="selectedFile"
        accept=".xlsx,.xls"
        placeholder="Chọn file Excel (.xlsx / .xls)"
        @change="onFileChange"
      />
      <small v-if="fileError" class="text-danger">{{ fileError }}</small>
    </div>

    <div v-else>
      <div class="d-flex align-items-center justify-content-between mb-2">
        <span>
          <b-badge variant="success">{{ validRows.length }} hợp lệ</b-badge>
          <b-badge variant="danger" class="ml-1" v-if="errorRows.length">{{ errorRows.length }} lỗi</b-badge>
          <span v-if="multiSheetWarning" class="ml-2 text-warning small">{{ multiSheetWarning }}</span>
        </span>
        <b-button size="sm" variant="outline-secondary" @click="reset">Chọn file khác</b-button>
      </div>
      <div class="table-responsive">
        <table class="table table-sm table-bordered">
          <thead class="thead-light">
            <tr>
              <th>#</th>
              <th>Khoản mục phí</th>
              <th>Cửa hàng</th>
              <th>Số tiền</th>
              <th>Số tháng SD</th>
              <th>Trạng thái</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in pagedRows"
              :key="(previewPage - 1) * previewPageSize + idx"
              :class="row._error ? 'table-danger' : ''"
            >
              <td>{{ (previewPage - 1) * previewPageSize + idx + 1 }}</td>
              <td>{{ row.rawCostItem }}</td>
              <td>{{ row.rawStore }}</td>
              <td>{{ row.rawAmount }}</td>
              <td>{{ row.rawMonths }}</td>
              <td>
                <span v-if="row._error" class="text-danger"><i class="fas fa-times-circle"></i> {{ row._error }}</span>
                <span v-else class="text-success"><i class="fas fa-check-circle"></i> OK</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="previewTotalPages > 1" class="d-flex align-items-center justify-content-between mt-2">
        <small class="text-muted">
          Hiển thị {{ (previewPage - 1) * previewPageSize + 1 }}–{{ Math.min(previewPage * previewPageSize, previewRows.length) }} / {{ previewRows.length }} dòng
        </small>
        <b-pagination
          v-model="previewPage"
          :total-rows="previewRows.length"
          :per-page="previewPageSize"
          size="sm"
          class="mb-0"
        />
      </div>
    </div>

    <template #modal-footer>
      <b-button variant="secondary" @click="$emit('close')">Hủy</b-button>
      <b-button
        variant="primary"
        :disabled="!previewRows.length || errorRows.length > 0 || validRows.length === 0"
        @click="confirmImport"
      >
        Xác nhận import {{ validRows.length }} dòng
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import xlsx from 'xlsx';
import { makeToastFaile, xoa_dau } from '@/utils/common';
import { BASE_URL } from '@/utils/constants';

const normalizeStr = (s) => xoa_dau((s || '').toString().trim()).toLowerCase();

export default {
  props: {
    costItemSource: { type: Array, default: () => [] },
    storeSource: { type: Array, default: () => [] },
  },
  data() {
    return {
      visible: true,
      selectedFile: null,
      fileError: '',
      previewRows: [],
      previewPage: 1,
      previewPageSize: 20,
      multiSheetWarning: '',
      urlTemplate: BASE_URL + 'templates/download?type=purchase-request-import',
    };
  },
  computed: {
    validRows() {
      return this.previewRows.filter((r) => !r._error);
    },
    errorRows() {
      return this.previewRows.filter((r) => r._error);
    },
    previewTotalPages() {
      return Math.ceil(this.previewRows.length / this.previewPageSize);
    },
    pagedRows() {
      const start = (this.previewPage - 1) * this.previewPageSize;
      return this.previewRows.slice(start, start + this.previewPageSize);
    },
  },
  methods: {
    makeToastFaile,
    reset() {
      this.selectedFile = null;
      this.fileError = '';
      this.previewRows = [];
      this.previewPage = 1;
      this.multiSheetWarning = '';
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate extension
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls'].includes(ext)) {
        this.fileError = 'Format không hỗ trợ. Chỉ chấp nhận .xlsx hoặc .xls';
        return;
      }

      // Validate size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.fileError = 'File quá lớn, max 5MB';
        return;
      }

      this.fileError = '';
      this.parseFile(file);
    },
    parseFile(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = xlsx.read(data, { type: 'array' });

          if (workbook.SheetNames.length > 1) {
            this.multiSheetWarning = `Chỉ đọc sheet đầu "${workbook.SheetNames[0]}", bỏ qua ${workbook.SheetNames.length - 1} sheet khác.`;
          }

          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const rows = xlsx.utils.sheet_to_json(sheet, { header: 1, defval: '' });

          if (rows.length <= 1) {
            this.fileError = 'File không có dữ liệu (chỉ có header hoặc trống).';
            return;
          }

          const dataRows = rows.slice(1).filter((row) => row.some((cell) => cell !== ''));

          if (dataRows.length > 100) {
            this.fileError = 'Tối đa 100 dòng/phiếu. File có ' + dataRows.length + ' dòng.';
            return;
          }

          this.previewRows = dataRows.map((row) => this.parseRow(row));
        } catch (err) {
          this.fileError = 'File không đọc được, thử template mẫu.';
        }
      };
      reader.readAsArrayBuffer(file);
    },
    parseRow(row) {
      const rawCostItem = (row[0] || '').toString().trim();
      const rawStore = (row[1] || '').toString().trim();
      const rawAmount = (row[2] || '').toString().trim();
      const rawMonths = (row[3] || '').toString().trim();

      const result = { rawCostItem, rawStore, rawAmount, rawMonths, _error: null };

      // Match costItem
      const costItemNorm = normalizeStr(rawCostItem);
      const matchedCostItem = this.costItemSource.find((c) => normalizeStr(c.name) === costItemNorm);
      if (!rawCostItem) {
        result._error = 'Khoản mục phí trống';
        return result;
      }
      if (!matchedCostItem) {
        result._error = `Không tìm thấy khoản mục phí "${rawCostItem}"`;
        return result;
      }

      // Match store (optional)
      let matchedStore = null;
      if (rawStore) {
        const storeNorm = normalizeStr(rawStore);
        matchedStore = this.storeSource.find(
          (s) => normalizeStr(s.name) === storeNorm || normalizeStr(s.shortName) === storeNorm,
        );
        if (!matchedStore) {
          result._error = `Không tìm thấy cửa hàng "${rawStore}"`;
          return result;
        }
      }

      // Parse amount
      const amountStr = rawAmount.replace(/[,\s]/g, '');
      const amount = parseFloat(amountStr);
      if (!rawAmount || isNaN(amount) || amount <= 0) {
        result._error = 'Số tiền không hợp lệ (phải > 0)';
        return result;
      }

      // Parse months
      let months = 1;
      if (rawMonths) {
        months = parseInt(rawMonths, 10);
        if (isNaN(months) || months < 1 || months > 12) {
          result._error = 'Số tháng SD phải từ 1-12';
          return result;
        }
      }

      return {
        ...result,
        costItemId: Number(matchedCostItem.id),
        storeId: matchedStore ? Number(matchedStore.id) : null,
        requestedAmount: amount,
        budgetUsagePeriod: months,
        _costItemModel: matchedCostItem.name,
        _storeModel: matchedStore
          ? (matchedStore.shortName ? `${matchedStore.name} (${matchedStore.shortName})` : matchedStore.name)
          : '',
      };
    },
    confirmImport() {
      this.$emit('import', this.validRows);
    },
  },
};
</script>
